/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { ArrowRight, BookOpen, FileText, Landmark, Percent, Repeat } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { api } from '../../api';
import type { DecimalString } from '../../api';
import type { FxRate, PriceBook, PriceRule, RegistryPrice, TaxRate } from '../../api/areas/commerce';
import { Badge } from '../../components/Badge';
import { DataTable, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { Loading } from '../../components/States';
import { formatCount, formatDateTime, parseDecimal } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { hubUrl } from '../../nav/hubs';
import { EndRegistryPriceButton, EndRuleButton, EndTaxRateButton, NewPriceBookButton, RegistryPriceButton, TaxDefaultButton, TaxRateButton } from '../operator/Pricing';
import { AddSpecificPriceButton, BookMatrix, BookMenu } from './PriceMatrix';
import { Amount, cellLink, CustomerRef, Label, mutedSmall, priceBookPath, RefreshButton, RichText, useEnumLabel, When } from './shared';
import { useAddonName } from '../services/shared';

/** "0.1" -> "10", "0.085" -> "8.5": exact decimal shift, no floating point. */
export function fractionToPercent(rate: string): string {
  const d = parseDecimal(rate);
  if (!d) return rate;
  const frac = d.fraction.padEnd(2, '0');
  const whole = (d.whole * 100n + BigInt(frac.slice(0, 2))).toString();
  const rest = frac.slice(2).replace(/0+$/, '');
  return `${d.negative ? '-' : ''}${whole}${rest ? `.${rest}` : ''}`;
}

function Window({ from, until }: { from: string; until: string | null }) {
  const { t } = useTranslation('commerce');
  const now = new Date().toISOString();
  const state =
    from > now ? (
      <Badge tone="info">{t('pricing.window.SCHEDULED')}</Badge>
    ) : until && until <= now ? (
      <Badge tone="neutral">{t('pricing.window.CLOSED')}</Badge>
    ) : (
      <Badge tone="ok">{t('pricing.window.IN_FORCE')}</Badge>
    );
  return (
    <span className="inline-flex flex-col gap-0.5" title={`${formatDateTime(from)} → ${until ? formatDateTime(until) : t('pricing.open')}`}>
      {state}
      <span className={`${mutedSmall} whitespace-nowrap`}>
        {t('pricing.from', { at: formatDateTime(from) })}
        {until ? ` ${t('pricing.until', { at: formatDateTime(until) })}` : ''}
      </span>
    </span>
  );
}

/** What a rule does to the price: a percentage markup, a fixed markup, or a fixed price. */
export function ruleValue(r: { rule_type?: string | null; value?: DecimalString | null }, currency: string) {
  if (r.rule_type === 'MARKUP_PERCENT') return <span className="tabular whitespace-nowrap">+{r.value} %</span>;
  if (r.rule_type === 'MARKUP_FIXED')
    return (
      <span className="whitespace-nowrap">
        +<Amount amount={r.value} currency={currency} />
      </span>
    );
  return <Amount amount={r.value} currency={currency} />;
}

/** The rules of one price book: which operation and TLD they apply to, and for how long. */
function PriceRulesTable({ rules, currency, empty, onChanged, hideTld }: { rules: PriceRule[]; currency: string; empty: string; onChanged?: () => void; hideTld?: boolean }) {
  const { t } = useTranslation('commerce');
  const addonName = useAddonName();
  const columns: Column<PriceRule>[] = [
    {
      key: 'operation',
      header: t('pricing.ruleCol.operation'),
      cell: (r) => (
        <span className="inline-flex flex-wrap items-center gap-1">
          <Label group="operation" value={r.operation} />
          {r.addon_code ? <span title={r.addon_code}>{addonName(r.addon_code)}</span> : null}
        </span>
      ),
    },
    ...(hideTld
      ? []
      : [{ key: 'tld', header: t('pricing.ruleCol.tld'), cell: (r: PriceRule) => (r.tld ? <span className="font-medium">.{r.tld}</span> : <span className="text-muted-foreground">{t('pricing.allTlds')}</span>) }]),
    { key: 'years', header: t('pricing.ruleCol.years'), cell: (r) => r.years ?? <span className="text-muted-foreground">{t('pricing.perYear')}</span> },
    {
      key: 'rule',
      header: t('pricing.ruleCol.rule'),
      cell: (r) => (
        <span className="text-xs whitespace-nowrap">
          <Label group="ruleType" value={r.rule_type} /> · v{r.version}
        </span>
      ),
    },
    { key: 'value', header: t('pricing.ruleCol.value'), numeric: true, cell: (r) => ruleValue(r, currency) },
    {
      key: 'bounds',
      header: t('pricing.ruleCol.bounds'),
      className: 'whitespace-nowrap text-xs',
      cell: (r) => (
        <>
          {r.min_amount ? (
            <div>
              {t('pricing.min')} <Amount amount={r.min_amount} currency={currency} />
            </div>
          ) : null}
          {r.max_amount ? (
            <div>
              {t('pricing.max')} <Amount amount={r.max_amount} currency={currency} />
            </div>
          ) : null}
          {!r.min_amount && !r.max_amount ? '—' : null}
        </>
      ),
    },
    { key: 'priority', header: t('pricing.ruleCol.priority'), numeric: true, cell: (r) => r.priority },
    { key: 'validity', header: t('pricing.ruleCol.validity'), cell: (r) => <Window from={r.effective_from} until={r.effective_until} /> },
    ...(onChanged ? [{ key: 'end', header: '', cell: (r: PriceRule) => <EndRuleButton rule={r} onChanged={onChanged} /> }] : []),
  ];
  return <DataTable columns={columns} rows={rules} rowKey={(r) => r.id} empty={<p className="text-sm text-muted-foreground">{empty}</p>} />;
}

/** Every rule of a book with its technical fields (priority, limits, version, window): for audit. */
function RuleDetails({ rules, currency, history, onChanged }: { rules: PriceRule[]; currency: string; history: boolean; onChanged: () => void }) {
  const { t } = useTranslation('commerce');
  return (
    <details className="group border-t">
      <summary className="cursor-pointer px-5 py-3 text-sm font-medium text-muted-foreground hover:text-foreground">
        {t('matrix.details')} ({rules.length})
      </summary>
      <PriceRulesTable rules={rules} currency={currency} empty={history ? t('pricing.noRules') : t('pricing.noRulesInForce')} onChanged={onChanged} />
    </details>
  );
}

/**
 * One price book: its name, what it applies to, the effective price of each TLD and operation
 * (a matrix; a cell opens the price dialog) and, folded away, the rules themselves.
 */
function PriceBookSection({ book: b, history, onChanged, tldId }: { book: PriceBook; history: boolean; onChanged: () => void; tldId?: string }) {
  const { t } = useTranslation('commerce');
  const label = useEnumLabel('commerce');
  const meta = [b.currency, label('appliesTo', b.applies_to), b.tier ? t('pricing.bookTier', { tier: b.tier }) : null, b.customer_name ? t('pricing.bookCustomer', { name: b.customer_name }) : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <Section
      flush
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          <Link to={priceBookPath(b.id)} className={`${cellLink} font-mono`}>
            {b.code}
          </Link>
          <Badge tone={b.status === 'ACTIVE' ? 'ok' : 'neutral'} title={b.status}>
            {label('bookStatus', b.status)}
          </Badge>
        </span>
      }
      description={meta}
    >
      {/* In the body, not the card header: the header's action column would overflow a phone. */}
      <div className="mb-3 flex flex-wrap items-center gap-2 px-5">
        <AddSpecificPriceButton book={b} onChanged={onChanged} />
        <Link to={priceBookPath(b.id)} className={`${cellLink} text-sm`}>
          {t('pricing.openBook')}
        </Link>
        <BookMenu book={b} onChanged={onChanged} />
      </div>
      <BookMatrix book={b} refreshKey={b.rules.length} onChanged={onChanged} />
      <RuleDetails
        // On a TLD's page: the rules for that TLD and those for every TLD (add-on prices are
        // in the TLD's add-on section).
        rules={tldId ? b.rules.filter((r) => (r.tld_id === tldId || r.tld_id === null) && r.operation !== 'ADDON') : b.rules}
        currency={b.currency}
        history={history}
        onChanged={onChanged}
      />
    </Section>
  );
}

/**
 * What names cost us and what we sell them for, with every version's window. `part` shows one
 * slice of it where the Settings hub gives each its own tab: registry costs (with FX rates),
 * selling price books, or tax rates; `prices` is everything but the tax rates. `tld` narrows
 * the costs and rules to one TLD (the TLD page's price tab).
 */
export function PricingPage({ part = 'all', tld }: { part?: 'all' | 'prices' | 'costs' | 'books' | 'tax'; tld?: { id: string; zone: string } }) {
  const showCosts = part === 'all' || part === 'prices' || part === 'costs';
  const showBooks = part === 'all' || part === 'prices' || part === 'books';
  const showTax = part === 'all' || part === 'tax';
  const { t } = useTranslation('commerce');
  useDocumentTitle(t('pricing.title'));
  const [params, setParams] = useSearchParams();
  const history = params.get('history') === '1';
  const result = useApi((signal) => api.getPricing({ history }, { signal }), [history]);
  const addonName = useAddonName();

  const registryColumns: Column<RegistryPrice>[] = [
    // On a TLD's page the zone would repeat the page on every row.
    ...(tld ? [] : [{ key: 'tld', header: t('pricing.registryCol.tld'), cell: (p: RegistryPrice) => <span className="font-medium">.{p.tld}</span> }]),
    {
      key: 'operation',
      header: t('pricing.registryCol.operation'),
      cell: (p) => (
        <span className="inline-flex flex-wrap items-center gap-1">
          <Label group="operation" value={p.operation} />
          {p.addon_code ? <span title={p.addon_code}>{addonName(p.addon_code)}</span> : null}
          {p.for_operation ? <span className="text-xs text-muted-foreground">({p.for_operation})</span> : null}
        </span>
      ),
    },
    { key: 'years', header: t('pricing.registryCol.years'), numeric: true, cell: (p) => p.years },
    { key: 'cost', header: t('pricing.registryCol.cost'), numeric: true, cell: (p) => (p.amount_basis === 'PERCENT_OF_REGISTRY_COST' ? <span className="tabular">{p.amount} %</span> : <Amount amount={p.amount} currency={p.currency} />) },
    { key: 'version', header: t('pricing.registryCol.version'), numeric: true, cell: (p) => `v${p.version}` },
    { key: 'validity', header: t('pricing.registryCol.validity'), cell: (p) => <Window from={p.effective_from} until={p.effective_until} /> },
    { key: 'end', header: '', cell: (p) => <EndRegistryPriceButton price={p} onChanged={result.reload} /> },
  ];
  const fxColumns: Column<FxRate>[] = [
    {
      key: 'pair',
      header: t('pricing.fxCol.pair'),
      cell: (f) => (
        <span className="font-mono whitespace-nowrap">
          1 {f.base_currency} → {f.quote_currency}
        </span>
      ),
    },
    { key: 'rate', header: t('pricing.fxCol.rate'), numeric: true, cell: (f) => <span className="font-mono">{f.rate}</span> },
    { key: 'source', header: t('pricing.fxCol.source'), cell: (f) => f.source },
    { key: 'effective', header: t('pricing.fxCol.effective'), className: 'whitespace-nowrap', cell: (f) => formatDateTime(f.effective_from) },
  ];
  const taxColumns: Column<TaxRate>[] = [
    { key: 'jurisdiction', header: t('pricing.taxCol.jurisdiction'), cell: (x) => x.jurisdiction },
    { key: 'code', header: t('pricing.taxCol.code'), cell: (x) => <code className="font-mono text-xs">{x.tax_code}</code> },
    {
      key: 'rate',
      header: t('pricing.taxCol.rate'),
      numeric: true,
      cell: (x) => (
        <span title={x.rate} className="whitespace-nowrap">
          {fractionToPercent(x.rate)} %
        </span>
      ),
    },
    { key: 'source', header: t('pricing.taxCol.source'), className: 'whitespace-normal', cell: (x) => <span className="block min-w-40 text-xs">{x.source_reference}</span> },
    { key: 'validity', header: t('pricing.taxCol.validity'), cell: (x) => <Window from={x.effective_from} until={x.effective_until} /> },
    {
      key: 'default',
      header: '',
      cell: (x) =>
        result.status === 'success' && result.data.tax_default === x.tax_code ? (
          <Badge tone="ok">{t('pricing.taxDefault')}</Badge>
        ) : x.effective_until === null ? (
          <TaxDefaultButton code={x.tax_code} current={result.status === 'success' ? result.data.tax_default : null} onChanged={result.reload} />
        ) : null,
    },
    {
      key: 'actions',
      header: '',
      cell: (x) => (
        <span className="inline-flex gap-1">
          {x.effective_until === null ? <TaxRateButton code={x.tax_code} onChanged={result.reload} /> : null}
          <EndTaxRateButton rate={x} onChanged={result.reload} />
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          tld
            ? t('pricing.forTld', { zone: tld.zone })
            : part === 'tax'
              ? t('pricing.taxRates')
              : part === 'costs'
                ? t('pricing.registryCost')
                : part === 'books'
                  ? t('pricing.priceBooks')
                  : t('pricing.title')
        }
        crumbs={tld ? undefined : [{ label: t('crumb') }]}
        subtitle={<RichText k="pricing.subtitle" />}
        actions={
          <>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              aria-label={t('pricing.validity')}
              value={history ? 'history' : 'current'}
              onValueChange={(v) => {
                if (v === 'current') setParams(new URLSearchParams());
                else if (v === 'history') setParams(new URLSearchParams({ history: '1' }));
              }}
            >
              <ToggleGroupItem value="current" className="px-3">
                {t('pricing.inForce')}
              </ToggleGroupItem>
              <ToggleGroupItem value="history" className="px-3">
                {t('pricing.withHistory')}
              </ToggleGroupItem>
            </ToggleGroup>
            <RefreshButton onClick={result.reload} />
            {tld ? (
              <Button asChild variant="outline" size="sm">
                <Link to={hubUrl('products', 'pricing')}>
                  {t('pricing.allTldsPrices')} <ArrowRight />
                </Link>
              </Button>
            ) : null}
            {showCosts ? <RegistryPriceButton onChanged={result.reload} tldId={tld?.id} /> : null}
            {showBooks ? <NewPriceBookButton onChanged={result.reload} /> : null}
          </>
        }
      />
      {result.status === 'loading' ? <Loading rows={4} /> : null}
      {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('pricing.loadError')} /> : null}
      {result.status === 'success' ? (
        <div className="flex flex-col gap-6">
          {part === 'all' ? (
            <StatGrid>
              <StatCard icon={Landmark} label={t('pricing.stat.registryPrices')} value={formatCount(result.data.registry_prices.length)} />
              <StatCard icon={BookOpen} label={t('pricing.stat.priceBooks')} value={formatCount(result.data.price_books.length)} />
              <StatCard icon={Repeat} label={t('pricing.stat.fxRates')} value={formatCount(result.data.fx_rates.length)} />
              <StatCard icon={Percent} label={t('pricing.stat.taxRates')} value={formatCount(result.data.tax_rates.length)} />
            </StatGrid>
          ) : null}

          {showCosts ? (
            <Section title={t('pricing.registryCost')} flush>
              <DataTable columns={registryColumns} rows={tld ? result.data.registry_prices.filter((p) => p.tld_id === tld.id && p.operation !== 'ADDON') : result.data.registry_prices} rowKey={(p) => p.id} empty={<p className="text-sm text-muted-foreground">{t('pricing.noRegistryPrices')}</p>} />
            </Section>
          ) : null}

          {showBooks ? (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold">{t('pricing.priceBooks')}</h2>
              {result.data.price_books.length === 0 ? <p className="text-sm text-muted-foreground">{t('pricing.noPriceBooks')}</p> : null}
              {result.data.price_books.map((b) => (
                <PriceBookSection key={b.id} book={b} history={history} onChanged={result.reload} tldId={tld?.id} />
              ))}
            </div>
          ) : null}

          {/* Full width, not side by side: both tables carry a validity window that a
              half-width card would push off the edge. */}
          <div className="flex flex-col gap-6">
            {showCosts ? (
              <Section title={t('pricing.fxRates')} flush>
                <DataTable columns={fxColumns} rows={result.data.fx_rates} rowKey={(f) => f.id} empty={<p className="text-sm text-muted-foreground">{t('pricing.noFx')}</p>} />
              </Section>
            ) : null}
            {showTax ? (
              <Section
                title={t('pricing.taxRates')}
                description={result.data.tax_default ? t('pricing.taxDefaultIs', { code: result.data.tax_default }) : t('pricing.taxNoDefault')}
                actions={<TaxRateButton onChanged={result.reload} variant="outline" />}
                flush
              >
                <DataTable columns={taxColumns} rows={result.data.tax_rates} rowKey={(x) => x.id} empty={<p className="text-sm text-muted-foreground">{t('pricing.noTax')}</p>} />
              </Section>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

/** `/pricing/books/:bookId`: one price book, every rule it has had, and how much it is used. */
export function PriceBookDetailPage() {
  const { t } = useTranslation('commerce');
  const label = useEnumLabel('commerce');
  const { bookId = '' } = useParams();
  useDocumentTitle(t('priceBook.title'));
  const result = useApi((signal) => api.getPriceBook(bookId, { signal }), [bookId]);
  const crumbs = [{ label: t('crumb') }, { label: t('pricing.title'), to: '/pricing' }];
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') {
    return (
      <>
        <PageHeader title={t('priceBook.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={result.reload} title={t('priceBook.loadError')} />
      </>
    );
  }
  const { book: b, quote_count, last_quoted_at } = result.data;
  return (
    <>
      <PageHeader
        title={<span className="font-mono">{b.code}</span>}
        crumbs={[...crumbs, { label: b.code }]}
        subtitle={`${label('appliesTo', b.applies_to)} · ${b.currency}`}
        actions={
          <>
            <RefreshButton onClick={result.reload} />
            <AddSpecificPriceButton book={b} onChanged={result.reload} />
            <BookMenu book={b} onChanged={result.reload} />
          </>
        }
      />
      <div className="flex flex-col gap-6">
        <StatGrid className="xl:grid-cols-3">
          <StatCard icon={BookOpen} label={t('priceBook.stat.rules')} value={formatCount(b.rules.length)} tone={b.rules.length ? 'neutral' : 'warn'} />
          <StatCard icon={FileText} label={t('priceBook.stat.quotes')} value={formatCount(quote_count)} />
          <StatCard icon={Percent} label={t('priceBook.stat.lastQuoted')} value={last_quoted_at ? <When at={last_quoted_at} /> : t('priceBook.never')} tone={last_quoted_at ? 'ok' : 'neutral'} />
        </StatGrid>
        <Section>
          <KeyValue
            columns={3}
            rows={[
              [
                t('priceBook.field.code'),
                <code key="c" className="font-mono">
                  {b.code}
                </code>,
              ],
              [
                t('common.status'),
                <Badge key="s" tone={b.status === 'ACTIVE' ? 'ok' : 'neutral'} title={b.status}>
                  {label('bookStatus', b.status)}
                </Badge>,
              ],
              [
                t('common.currency'),
                <code key="cu" className="font-mono">
                  {b.currency}
                </code>,
              ],
              [t('priceBook.field.appliesTo'), label('appliesTo', b.applies_to)],
              [t('priceBook.field.tier'), b.tier],
              [t('priceBook.field.customer'), b.customer_id ? <CustomerRef key="c" id={b.customer_id} name={b.customer_name} /> : null],
              [t('priceBook.field.created'), formatDateTime(b.created_at)],
            ]}
          />
        </Section>
        <Section title={t('priceBook.rules')} flush>
          <BookMatrix book={b} refreshKey={b.rules.length} onChanged={result.reload} />
          <RuleDetails rules={b.rules} currency={b.currency} history onChanged={result.reload} />
        </Section>
      </div>
    </>
  );
}
